import { useState } from "preact/hooks"
import { Loader2, Pencil, Receipt } from "lucide-react"
import { Button } from "../../../../../components/ui/button"
import { Input } from "../../../../../components/ui/input"
import { parse_money_input } from "./promotion_performance_editor_utils"

export type EditableInsightKpiKind = "planned" | "actual" | "readonly" | "metrics"

interface EditableInsightKpiProps {
  label: string
  value: string
  tone?: "good" | "bad"
  kind: EditableInsightKpiKind
  /** Small caption under the value, e.g. the plan-time expectation the measured number is judged against. */
  note?: string
  planned_amount?: number | null
  on_save_planned?: (amount: number | null) => Promise<void>
  on_open_metrics?: () => void
  on_match_deductions?: () => void
}

export function EditableInsightKpi({
  label,
  value,
  tone,
  kind,
  note,
  planned_amount,
  on_save_planned,
  on_open_metrics,
  on_match_deductions,
}: EditableInsightKpiProps) {
  const [editing, set_editing] = useState(false)
  const [draft, set_draft] = useState(format_money_input(planned_amount))
  const [saving, set_saving] = useState(false)

  const tone_class = tone === "good" ? "text-green-700" : tone === "bad" ? "text-red-700" : "text-gray-900"

  async function handle_save_planned() {
    if (!on_save_planned) return
    set_saving(true)
    try {
      await on_save_planned(parse_money_input(draft))
      set_editing(false)
    } finally {
      set_saving(false)
    }
  }

  return (
    <div class="rounded-md border border-gray-200 p-3">
      <div class="flex items-center justify-between gap-2">
        <div class="text-xs font-medium uppercase text-gray-500">{label}</div>
        {kind === "planned" ? (
          <button type="button" class="text-gray-400 hover:text-gray-700" onClick={() => set_editing(true)} aria-label={`Edit ${label}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        ) : null}
        {kind === "metrics" ? (
          <button type="button" class="text-gray-400 hover:text-gray-700" onClick={on_open_metrics} aria-label={`Edit ${label}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>

      {kind === "planned" && editing ? (
        <div class="mt-2 space-y-2">
          <Input
            type="number"
            step="0.01"
            value={draft}
            onInput={(event: Event) => set_draft((event.target as HTMLInputElement).value)}
          />
          <div class="flex gap-2">
            <Button onClick={handle_save_planned} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
            <Button variant="outline" onClick={() => set_editing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div class={`mt-1 text-lg font-semibold ${tone_class}`}>{value}</div>
      )}
      {note ? <div class="mt-1 text-xs text-gray-500">{note}</div> : null}

      {kind === "actual" ? (
        <Button variant="link" className="mt-2 h-auto p-0 text-xs" onClick={on_match_deductions}>
          <Receipt className="mr-1 h-3.5 w-3.5" />
          Match deductions
        </Button>
      ) : null}
    </div>
  )
}

function format_money_input(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return ""
  return String(value)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("EditableInsightKpi helpers", () => {
    it("formats planned amount drafts", () => {
      expect(format_money_input(1500)).toBe("1500")
      expect(format_money_input(null)).toBe("")
    })
  })
}
